import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import type {
  CompleteUploadRequest,
  CompleteUploadResponse,
  GenerateDownloadUrlResponse,
  GenerateUploadUrlRequest,
  GenerateUploadUrlResponse,
  GetFileByIdResponse,
  GetFilesRequest,
  GetFilesResponse,
} from '@mai/platform-api/src/routes/files.schema';

import { type QueryOptions, apiClient } from './axios';

// Type definitions from platform API schemas
export type { GetFilesRequest, GetFilesResponse };

// Query keys
export const filesQueryKeys = {
  all: ['files'] as const,
  lists: () => [...filesQueryKeys.all, 'list'] as const,
  list: (filters: GetFilesRequest) =>
    [...filesQueryKeys.lists(), filters] as const,
  details: () => [...filesQueryKeys.all, 'detail'] as const,
  detail: (fileId: string) => [...filesQueryKeys.details(), fileId] as const,
};

// API functions
export const fetchFiles = async (
  params?: Partial<GetFilesRequest>,
): Promise<GetFilesResponse> => {
  const response = await apiClient.get('/files', { params });
  return response.data;
};

export const fetchFilesByTeamId = async (
  teamId: string,
): Promise<GetFilesResponse> => {
  return fetchFiles({ teamIds: [teamId] });
};

export const fetchFileById = async (
  fileId: string,
): Promise<GetFileByIdResponse> => {
  const response = await apiClient.get(`/files/${fileId}`);
  return response.data;
};

export const deleteFile = async (fileId: string): Promise<void> => {
  await apiClient.delete(`/files/${fileId}`);
};

export const generateUploadUrl = async (
  params: GenerateUploadUrlRequest,
): Promise<GenerateUploadUrlResponse> => {
  const response = await apiClient.post('/files/upload-url', params);
  return response.data;
};

export const completeUpload = async (
  fileId: string,
  params: CompleteUploadRequest,
): Promise<CompleteUploadResponse> => {
  const response = await apiClient.post(`/files/${fileId}/complete`, params);
  return response.data;
};

export const getDownloadUrl = async (
  fileId: string,
): Promise<GenerateDownloadUrlResponse> => {
  const response = await apiClient.get(`/files/${fileId}/download`);
  return response.data;
};

// Query hooks
export const useFiles = (
  params?: Partial<GetFilesRequest>,
  options?: QueryOptions,
) => {
  return useQuery({
    queryKey: filesQueryKeys.list((params as GetFilesRequest) || {}),
    queryFn: () => fetchFiles(params),
    enabled: options?.enabled !== false,
    retry: options?.retry,
    placeholderData: keepPreviousData,
  });
};

export const useFilesByTeamId = (teamId: string, options?: QueryOptions) => {
  return useFiles({ teamIds: [teamId] }, options);
};

export const useFile = (fileId: string, options?: QueryOptions) => {
  return useQuery({
    queryKey: filesQueryKeys.detail(fileId),
    queryFn: () => fetchFileById(fileId),
    enabled: !!fileId && options?.enabled !== false,
    retry: options?.retry,
  });
};

// Mutation hooks
export const useDeleteFile = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteFile,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: filesQueryKeys.lists() });
    },
  });
};

export const useGenerateUploadUrl = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: generateUploadUrl,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: filesQueryKeys.lists() });
    },
  });
};

export const useCompleteUpload = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      fileId,
      ...params
    }: CompleteUploadRequest & { fileId: string }) =>
      completeUpload(fileId, params),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: filesQueryKeys.lists() });
    },
  });
};

// Helper to download a file
export async function downloadFile(fileId: string, fileName: string) {
  const { downloadUrl } = await getDownloadUrl(fileId);

  const link = document.createElement('a');
  link.href = downloadUrl;
  link.setAttribute('download', fileName);
  document.body.appendChild(link);
  link.click();
  link.remove();
}

// Helper to upload file to S3
export async function uploadFileToS3(
  uploadUrl: string,
  file: globalThis.File,
  onProgress?: (progress: number) => void,
) {
  const xhr = new XMLHttpRequest();

  return new Promise<void>((resolve, reject) => {
    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable && onProgress) {
        const progress = Math.round((event.loaded / event.total) * 100);
        onProgress(progress);
      }
    });

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new Error(`Upload failed with status ${xhr.status}`));
      }
    });

    xhr.addEventListener('error', () => {
      reject(new Error('Upload failed'));
    });

    xhr.open('PUT', uploadUrl);
    xhr.setRequestHeader(
      'Content-Type',
      file.type || 'application/octet-stream',
    );
    xhr.send(file);
  });
}
